/**
 * AdminProfileBadge · v2.7.6
 * ──────────────────────────
 * Interactive header profile component for the Admin Console.
 *
 * Visual:
 *   ┌─ avatar circle ─┐
 *   │ ●pulse-dot-green│  First Last
 *   │ (or solid red)  │  SUPERVISOR
 *   └─────────────────┘  Online · America/Chicago
 *
 * Click the avatar → modal:
 *   - Upload / replace / remove avatar image
 *   - First name + Last name inputs
 *   - Online / Offline manual toggle
 *
 * Heartbeats every 30 s while mounted so the server-derived status stays
 * fresh. On unmount or window blur, status falls to "offline" naturally
 * after the 90 s threshold elapses (no explicit goodbye needed).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import adminApi from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Bell, BellOff, Camera, Loader2, Trash2, UserCircle2 } from "lucide-react";
import { toast } from "sonner";
import { PROFILE_UPDATED_EVENT, announceProfileUpdated } from "@/components/admin/SidebarProfileFooter";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const ACCEPT = "image/png,image/jpeg,image/webp";
const MAX_BYTES = 8 * 1024 * 1024;
const HEARTBEAT_MS = 30_000;


function toAssetURL(maybeRelative) {
  if (!maybeRelative) return "";
  if (/^https?:\/\//i.test(maybeRelative)) return maybeRelative;
  return `${BASE}${maybeRelative}`;
}

function initialsOf(fn, ln, fallback) {
  const a = (fn || "").trim();
  const b = (ln || "").trim();
  if (a && b) return (a[0] + b[0]).toUpperCase();
  if (a) return a[0].toUpperCase();
  if (b) return b[0].toUpperCase();
  return (fallback || "?").slice(0, 1).toUpperCase();
}


export default function AdminProfileBadge({ adminFallback }) {
  const [profile, setProfile] = useState(null);
  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingAvatar, setSavingAvatar] = useState(false);
  const [togglingMute, setTogglingMute] = useState(false);
  const fileRef = useRef(null);

  // v2.7.6+ · Mirror server `audio_muted` into localStorage so the audio
  // engine can read it synchronously on every event without a round-trip.
  useEffect(() => {
    if (typeof window === "undefined" || !profile) return;
    const muted = !!profile.audio_muted;
    try { window.localStorage.setItem("rtg_admin_audio_muted", muted ? "1" : "0"); } catch {/* noop */}
    try { window.dispatchEvent(new CustomEvent("rtg:audio_mute_changed", { detail: { muted }})); } catch {/* noop */}
  }, [profile?.audio_muted]);  // eslint-disable-line react-hooks/exhaustive-deps

  const reload = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/me/profile");
      setProfile(data);
    } catch (e) { /* silent — header keeps falling back to the auth-payload values */ }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    window.addEventListener(PROFILE_UPDATED_EVENT, reload);
    return () => window.removeEventListener(PROFILE_UPDATED_EVENT, reload);
  }, [reload]);

  // 30 s heartbeat while the badge is mounted.
  useEffect(() => {
    const tick = async () => {
      try {
        const { data } = await adminApi.post("/admin/me/heartbeat");
        setProfile(data);
      } catch { /* silent */ }
    };
    const id = setInterval(tick, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, []);

  // Sync form inputs when the modal opens with fresh data.
  useEffect(() => {
    if (open && profile) {
      setFirstName(profile.first_name || "");
      setLastName(profile.last_name || "");
    }
  }, [open, profile]);

  const status = profile?.status || "offline";
  const isOnline = status === "online";
  const firstNameDisplay = profile?.first_name || (adminFallback?.name || adminFallback?.username || "").split(" ")[0] || "";
  const lastNameDisplay  = profile?.last_name  || ((adminFallback?.name || "").split(" ").slice(1).join(" ")) || "";
  const fullName = (firstNameDisplay + " " + lastNameDisplay).trim() || adminFallback?.name || adminFallback?.username || "—";
  const roleLabel = (profile?.role || adminFallback?.role || "").toUpperCase();

  const avatarSrc = profile?.profile_image_url ? toAssetURL(profile.profile_image_url) : "";

  const onAvatarFile = async (file) => {
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      toast.error("Use PNG, JPEG, or WebP."); return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`File too large (${(file.size/1024/1024).toFixed(1)} MB). Max 8 MB.`); return;
    }
    setSavingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post("/admin/me/profile/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfile(data);
      announceProfileUpdated();
      toast.success("Profile picture updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Avatar upload failed");
    } finally { setSavingAvatar(false); }
  };

  const removeAvatar = async () => {
    setSavingAvatar(true);
    try {
      const { data } = await adminApi.delete("/admin/me/profile/avatar");
      setProfile(data);
      announceProfileUpdated();
      toast.success("Avatar removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove avatar");
    } finally { setSavingAvatar(false); }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const { data } = await adminApi.put("/admin/me/profile", {
        first_name: firstName, last_name: lastName,
      });
      setProfile(data);
      announceProfileUpdated();
      toast.success("Profile saved");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingProfile(false); }
  };

  // v2.7.6 · Manual status toggle removed per spec — status is now strictly
  // derived from the heartbeat pipeline; the badge is a presence indicator,
  // not a user-controlled switch.

  // v2.7.6+ · Per-user audio mute. Suppresses operational chimes (panic,
  // booking, driver-online, chat) without affecting any other user.
  const toggleMute = async () => {
    setTogglingMute(true);
    try {
      const next = !(profile?.audio_muted);
      const { data } = await adminApi.post("/admin/me/preferences", { audio_muted: next });
      setProfile(data);
      toast.success(`Operational audio ${next ? "MUTED" : "UNMUTED"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mute toggle failed");
    } finally { setTogglingMute(false); }
  };

  // v2.7.7 · Temporary snooze. Mutes for N minutes, then a setTimeout (and a
  // server-side `audio_muted_until` timestamp) auto-unmutes. Click again to
  // cancel.
  const snoozeFor = async (minutes) => {
    setTogglingMute(true);
    try {
      const until = new Date(Date.now() + minutes * 60_000).toISOString();
      const { data } = await adminApi.post("/admin/me/preferences", {
        audio_muted: true, audio_muted_until: until,
      });
      setProfile(data);
      toast.success(`Audio snoozed for ${minutes} min`);
      // Local timer also auto-clears in case the user keeps the tab open.
      setTimeout(async () => {
        try {
          const { data: d2 } = await adminApi.post("/admin/me/preferences", {
            audio_muted: false, audio_muted_until: "",
          });
          setProfile(d2);
          toast.info("Audio snooze elapsed · chimes restored");
        } catch { /* silent */ }
      }, minutes * 60_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Snooze failed");
    } finally { setTogglingMute(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-full p-0.5 hover:bg-zinc-800/60 transition-colors group"
        data-testid="admin-profile-badge"
        title={`${fullName} · ${roleLabel} · ${isOnline ? "Online" : "Offline"}`}
      >
        {/* Avatar circle + status dot — visual-only, no text beside it. */}
        <div className="relative h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0">
          <div
            className={`h-full w-full rounded-full overflow-hidden border-2 ${
              isOnline ? "border-emerald-400/60" : "border-zinc-600"
            } bg-zinc-800 flex items-center justify-center group-hover:border-amber-400/60 transition-colors`}
            data-testid="admin-profile-avatar"
          >
            {avatarSrc ? (
              <img src={avatarSrc} alt={fullName} className="h-full w-full object-cover"/>
            ) : (
              <span className="text-[11px] font-bold text-amber-300" data-testid="admin-profile-initials">
                {initialsOf(firstNameDisplay, lastNameDisplay, adminFallback?.username)}
              </span>
            )}
          </div>
          <span
            className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-[#0c0d10] ${
              isOnline ? "bg-emerald-400 admin-status-pulse" : "bg-red-500"
            }`}
            data-testid="admin-status-dot"
            data-status={status}
            aria-label={`Status: ${status}`}
          />
          {profile?.audio_muted && (
            <span
              className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 border-2 border-[#0c0d10] flex items-center justify-center"
              title="Audio muted"
              data-testid="admin-profile-muted-indicator"
            >
              <BellOff className="h-2 w-2 text-white"/>
            </span>
          )}
        </div>
      </button>

      {/* Edit Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-amber-400/40 max-w-md" data-testid="admin-profile-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCircle2 className="h-5 w-5 text-amber-400"/>Edit Profile
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Avatar uploader */}
            <div className="flex items-center gap-4">
              <div className="relative h-20 w-20 flex-shrink-0">
                <div className="h-full w-full rounded-full overflow-hidden border-2 border-amber-400/40 bg-zinc-800 flex items-center justify-center">
                  {avatarSrc ? (
                    <img src={avatarSrc} alt={fullName} className="h-full w-full object-cover"/>
                  ) : (
                    <span className="text-2xl font-bold text-amber-300">
                      {initialsOf(firstNameDisplay, lastNameDisplay, adminFallback?.username)}
                    </span>
                  )}
                </div>
                <span
                  className={`absolute -bottom-0.5 -right-0.5 h-5 w-5 rounded-full border-2 border-zinc-900 ${
                    isOnline ? "bg-emerald-400 admin-status-pulse" : "bg-red-500"
                  }`}
                />
              </div>
              <div className="flex-1 space-y-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept={ACCEPT}
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onAvatarFile(f);
                    e.target.value = "";
                  }}
                  data-testid="admin-profile-file-input"
                />
                <Button
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  disabled={savingAvatar}
                  className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold w-full"
                  data-testid="admin-profile-upload-btn"
                >
                  {savingAvatar ? <Loader2 className="h-4 w-4 animate-spin"/> : (
                    <><Camera className="h-3.5 w-3.5 mr-1"/>Upload Photo</>
                  )}
                </Button>
                {avatarSrc && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={removeAvatar}
                    disabled={savingAvatar}
                    className="bg-zinc-950 border-zinc-700 text-red-400 hover:border-red-400/40 w-full text-[11px]"
                    data-testid="admin-profile-remove-avatar"
                  >
                    <Trash2 className="h-3 w-3 mr-1"/>Remove Photo
                  </Button>
                )}
              </div>
            </div>

            {/* Identity fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">First Name</Label>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First"
                  className="bg-zinc-950 border-zinc-700 mt-1"
                  data-testid="admin-profile-first-name"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Last Name</Label>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last"
                  className="bg-zinc-950 border-zinc-700 mt-1"
                  data-testid="admin-profile-last-name"
                />
              </div>
            </div>

            {/* v2.7.6 · Role + status are read-only inside the modal — staff
                cannot manually toggle availability per spec. Status is
                strictly heartbeat-derived from the active session. */}
            <div className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 space-y-2">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Position / Role</Label>
                <div className="mt-0.5 text-sm font-bold text-amber-400 uppercase tracking-wider" data-testid="admin-profile-modal-role">
                  {roleLabel || "STAFF"}
                </div>
              </div>
              <div className="flex items-center gap-2 pt-1 border-t border-zinc-800">
                <span className={`h-2.5 w-2.5 rounded-full ${isOnline ? "bg-emerald-400 admin-status-pulse" : "bg-red-500"}`}/>
                <span className={`text-xs font-bold ${isOnline ? "text-emerald-300" : "text-red-300"}`} data-testid="admin-profile-modal-status">
                  {isOnline ? "Online" : "Offline"}
                </span>
                <span className="text-[10px] text-zinc-500 ml-auto">Auto · driven by session heartbeat</span>
              </div>
            </div>

            {/* v2.7.6+ · Per-user audio mute toggle. Persists server-side and
                broadcasts a `rtg:audio_mute_changed` window event so the
                audio engine respects it instantly. */}
            <div className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  {profile?.audio_muted
                    ? <BellOff className="h-4 w-4 text-red-400 flex-shrink-0"/>
                    : <Bell className="h-4 w-4 text-emerald-400 flex-shrink-0"/>}
                  <div className="min-w-0">
                    <div className="text-sm font-bold">Operational Audio</div>
                    <div className="text-[10px] text-zinc-500">
                      {profile?.audio_muted_until
                        ? `Snoozed until ${new Date(profile.audio_muted_until).toLocaleTimeString([], {hour:"numeric", minute:"2-digit"})}`
                        : profile?.audio_muted
                          ? "Muted · chimes suppressed"
                          : "Active · all chimes will play"}
                    </div>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={toggleMute}
                  disabled={togglingMute}
                  className={`text-[11px] flex-shrink-0 ${profile?.audio_muted
                    ? "bg-zinc-950 border-red-400/40 text-red-300 hover:bg-red-400/10"
                    : "bg-zinc-950 border-zinc-700 hover:border-amber-400 hover:text-amber-400"}`}
                  data-testid="admin-profile-toggle-mute"
                >
                  {togglingMute
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin"/>
                    : profile?.audio_muted ? "Unmute" : "Mute"}
                </Button>
              </div>
              {/* v2.7.7 · Temporary snooze buttons — auto-unmute timers. */}
              {!profile?.audio_muted && (
                <div className="flex items-center gap-1.5 pt-2 border-t border-zinc-800">
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500 mr-1">Snooze:</span>
                  <Button size="sm" variant="outline" onClick={() => snoozeFor(15)} disabled={togglingMute} className="h-6 text-[10px] bg-zinc-950 border-zinc-700 hover:border-amber-400" data-testid="admin-profile-snooze-15">15 min</Button>
                  <Button size="sm" variant="outline" onClick={() => snoozeFor(30)} disabled={togglingMute} className="h-6 text-[10px] bg-zinc-950 border-zinc-700 hover:border-amber-400" data-testid="admin-profile-snooze-30">30 min</Button>
                  <Button size="sm" variant="outline" onClick={() => snoozeFor(60)} disabled={togglingMute} className="h-6 text-[10px] bg-zinc-950 border-zinc-700 hover:border-amber-400" data-testid="admin-profile-snooze-60">1 hr</Button>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="bg-zinc-950 border-zinc-700"
            >Cancel</Button>
            <Button
              onClick={saveProfile}
              disabled={savingProfile}
              className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold"
              data-testid="admin-profile-save"
            >
              {savingProfile ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : "SAVE"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
